// Reduces a gadget's raw aggregation rows to the small digest the Insights agent
// receives — so "why is Dallas low?" can be answered without the agent re-running
// the query and possibly getting a different number.
//
// A digest, never the rows: a daily trend over a year is 365 of them, and a dozen
// gadgets' worth would both blow the token budget and bury the signal.

import { getSeriesDataKey } from 'components/V5Gadget/seriesDataKey';
import {
  InsightsViewValueRow,
  InsightsViewValues,
} from 'contextProviders/InsightsContentProvider/viewTypes';

import pinRows from './pinRows';

// Rows kept per gadget. For a categorical breakdown these are the extremes (the
// interesting ones for "why is X low"); for a time series, the start and end.
export const MAX_DIGEST_ROWS = 8;
const HALF = MAX_DIGEST_ROWS / 2;

type Row = { [key: string]: string | number | FilterInput };

const toNumber = (raw: unknown): number | undefined => {
  const value = typeof raw === 'string' ? Number(raw) : raw;
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
};

// Which key in a row carries the row's label. Prefer the chart's own dimension;
// fall back to `date` for a trend. Returns undefined for a single total.
const resolveLabelField = ({
  chartDef,
  row,
}: {
  chartDef: V5ChartDefinition;
  row: Row;
}): string | undefined => {
  const dimensionField = chartDef.dimensionA?.field;
  if (dimensionField && row[dimensionField] !== undefined) {
    return dimensionField;
  }
  if (row.date !== undefined) {
    return 'date';
  }
  return undefined;
};

// Keep the head and tail rather than an arbitrary prefix: for a breakdown sorted
// by value that's the best and worst performers, and for a time series it's the
// start and end of the trend. Both are what a "why" question is about.
const sampleRows = <T>(rows: T[]): T[] =>
  rows.length <= MAX_DIGEST_ROWS
    ? rows
    : [...rows.slice(0, HALF), ...rows.slice(-HALF)];

const buildGadgetValuesDigest = ({
  chartDef,
  data,
  asOf,
  pinnedLabels,
}: {
  chartDef: V5ChartDefinition;
  data: V5ChartData;
  asOf: string;
  // Rows that must survive sampling (the value the user clicked). Appended after
  // the head/tail sample when they'd otherwise be dropped, so the digest can run
  // to MAX_DIGEST_ROWS + 1; rowsTruncatedFrom still reports the full count.
  pinnedLabels?: string[];
}): InsightsViewValues | undefined => {
  // Every series' rows are lined up on the chart's axis (series with query
  // overrides are fetched separately, see useV5ChartData), so the first gives
  // the row set; each series' value is read off its own row at that position.
  const series = chartDef.series
    .map((s) => ({ metricId: s.metricId, entry: data[getSeriesDataKey(s)] }))
    .filter((s): s is { metricId: string; entry: V5ChartData[string] } =>
      Boolean(s.entry),
    );
  if (series.length === 0) {
    return undefined;
  }

  const rows = (series[0].entry.response ?? []) as Row[];
  if (rows.length === 0) {
    return undefined;
  }

  const labelField = resolveLabelField({ chartDef, row: rows[0] });

  if (!labelField) {
    // No dimension and no trend — a single figure (e.g. a smart card).
    const first = series[0];
    const value = toNumber(rows[0][first.metricId]);
    if (value === undefined) {
      return undefined;
    }
    return { asOf, total: { metric: first.entry.name, value } };
  }

  const labelled = rows
    .map((row, index): InsightsViewValueRow | undefined => {
      const label = row[labelField];
      const values = series
        .map((s) => {
          const seriesRow = (s.entry.response[index] ?? {}) as Row;
          return {
            metric: s.entry.name,
            value: toNumber(seriesRow[s.metricId]),
          };
        })
        .filter(
          (v): v is { metric: string; value: number } => v.value !== undefined,
        );
      return typeof label === 'string' || typeof label === 'number'
        ? { label: String(label), values }
        : undefined;
    })
    .filter(
      (row): row is InsightsViewValueRow => !!row && row.values.length > 0,
    );

  if (labelled.length === 0) {
    return undefined;
  }

  const sampled = pinRows({
    sampled: sampleRows(labelled),
    all: labelled,
    pinnedLabels,
  });

  return {
    asOf,
    rows: sampled,
    ...(labelled.length > sampled.length
      ? { rowsTruncatedFrom: labelled.length }
      : {}),
  };
};

export default buildGadgetValuesDigest;
